import { useMemo } from 'react';

/**
 * Splits the hoisted props out of the props object
 * @param object - the object to split up
 * @param keys - a list of object keys to pull out of the object
 * @usage: const [rootProps, hoistedProps] = usePropSplit(props, HOISTED_PROPS);
 *
 * @See https://github.com/microsoft/TypeScript/issues/20965#issuecomment-570070201
 */
export function usePropSplit<R, P extends R>(object: P & { [s: string]: unknown; } | ArrayLike<unknown>, keys: ReadonlyArray<keyof P>): [Omit<P, keyof R>, R] {
  return useMemo(() => Object.entries(object).reduce(([remaining, abstracted], [name, val]) => {
    if (keys.includes(name as keyof P)) return [remaining, { ...abstracted, [name]: val }];

    return [{ ...remaining, [name]: val }, abstracted];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [{}, {}] as [Omit<P, keyof R>, R]), [keys, object]);
}
